import { Location, History } from "history";
import {DeptPaginationResponse} from "../components/workTableDepts/DeptList";

export const DEFAULT_PAGE = 1;
export const DEFAULT_LIMIT = 10;
export const DEFAULT_SORT_COLUMN = "name";

/**
 * Интерфейс базового состояния для страниц с пагинацией, сортировкой и поиском.
 * Используется как основа для состояния компонентов списков.
 * @property {number} page - Текущая страница (начинается с 1).
 * @property {number} limit - Количество элементов на странице.
 * @property {string} sortColumn - Имя колонки для сортировки. Может содержать префикс "-" для обратного порядка (например, "-surname").
 * @property {string} searchValue - Текст поискового запроса.
 */
export interface CommonListPageState {
    page: number;
    limit: number;
    sortColumn: string;
    searchValue?: string;
}

/**
 * Универсальный тип состояния для списков: пагинация + данные + загрузка.
 * Используется в компонентах, которые отображают списки с пагинацией.
 * @template T — тип элемента списка (например, Institution, Patient)
 */
export interface ListState<T> extends CommonListPageState {
    docs: T[];
    totalDocs: number;
    totalPages: number;
    loading: boolean;
    error: string | null;
}

/**
 * Ответ от API для страниц с пагинацией.
 * Описывает стандартную структуру постраничного списка элементов.
 * @template T - Тип элемента в массиве docs.
 * @property {T[]} docs - Массив элементов текущей страницы.
 * @property {number} totalDocs - Общее количество элементов по всем страницам.
 * @property {number} limit - Лимит элементов на странице.
 * @property {number} page - Текущая страница (начинается с 1).
 * @property {number} totalPages - Общее количество страниц.
 * @property {boolean} hasNextPage - Флаг наличия следующей страницы.
 * @property {number} nextPage - Номер следующей страницы (если есть).
 * @property {boolean} hasPrevPage - Флаг наличия предыдущей страницы.
 * @property {number} prevPage - Номер предыдущей страницы (если есть).
 * @property {number} pagingCounter - Позиция первого элемента текущей страницы в общем списке (начиная с 1).
 */
export interface CommonListPagePaginationResponse<T> {
    docs: T[];
    totalDocs: number;
    limit: number;
    page: number;
    totalPages: number;
    hasNextPage: boolean;
    nextPage: number;
    hasPrevPage: boolean;
    prevPage: number;
    pagingCounter: number;
}

/**
 * Ссылка на удаляемый объект: идентификатор и отображаемое имя.
 * Используется для диалога подтверждения удаления.
 */
export type ObjectReference = Readonly<{_id: string; name: string}>;

/**
 * Очищает URL от домена и протокола, оставляя только путь и параметры.
 * Используется для безопасного редиректа внутри приложения.
 * @param url - Исходный URL (может быть полным URL или относительным путем)
 * @param defaultUrl - URL по умолчанию, который вернется в случае ошибки парсинга
 * @returns Относительный путь с параметрами (например, "/patients?page=1") или defaultUrl при ошибке
 */
export const getCleanReturnUrl = (url: string, defaultUrl: string): string => {
    try {
        // Если URL содержит домен, извлекаем только путь
        if (url.includes('://')) {
            const urlObj = new URL(url);
            console.log(urlObj.pathname, urlObj.search)
            return urlObj.pathname + urlObj.search;
        }
        // Если это уже путь, возвращаем как есть
        return url;
    } catch {
        return defaultUrl; // fallback при ошибке
    }
};

/**
 * Проверяет, является ли URL валидным внутренним путем для приложения.
 * Валидный путь должен начинаться с '/'.
 * @param url - URL для проверки
 * @returns true, если URL валиден (начинается с '/'), иначе false
 */
export const isValidReturnUrl = (url: string): boolean => {
    // Проверяем, что это валидный путь (начинается с /)
    return url.startsWith('/');
};

/**
 * Извлекает параметр returnUrl из URL и возвращает очищенный и валидированный URL.
 * Если returnUrl отсутствует или невалиден, возвращает fallbackUrl.
 * @param location - Объект location из react-router
 * @param fallbackUrl - URL по умолчанию, который вернется если returnUrl отсутствует или невалиден
 * @returns Очищенный и валидированный URL для редиректа
 */
export const extractReturnUrl = (location: Location, fallbackUrl: string): string => {
    const urlParams = new URLSearchParams(location.search);
    const returnUrl = urlParams.get("returnUrl");

    if (returnUrl) {
        const cleanUrl = getCleanReturnUrl(returnUrl, fallbackUrl);
        if (isValidReturnUrl(cleanUrl)) {
            console.log('cleanUrl', cleanUrl)
            return cleanUrl;
        }
    }

    return fallbackUrl;
};

/**
 * Обработчик возврата назад — переходит по returnUrl, если валиден, иначе по fallbackUrl.
 * Используется для навигации "назад" с сохранением контекста.
 * @param history - Объект history из react-router для навигации
 * @param returnUrl - URL, по которому нужно перейти (обычно извлеченный из параметров)
 * @param fallbackUrl - URL по умолчанию, если returnUrl невалиден
 */
export const handleGoBack = (
    history: History,
    returnUrl: string,
    fallbackUrl: string
): void => {
    const targetUrl = isValidReturnUrl(returnUrl) ? returnUrl : fallbackUrl;
    console.log(targetUrl)
    history.push(targetUrl);
};

/**
 * Синхронизирует состояние пагинации с параметрами URL.
 * Парсит параметры page, limit, sort, order, search из URL и возвращает объект состояния.
 * @param search - Строка запроса из location.search (например, "?page=2&limit=20")
 * @param defaultSortColumn - Колонка сортировки по умолчанию (например, 'name' или 'surname')
 * @returns Объект с состоянием пагинации {page, limit, sortColumn, searchValue}
 */
export const syncPaginationStateWithUrl = (
    search: string,
    defaultSortColumn: string = ''
): {
    page: number;
    limit: number;
    sortColumn: string;
    searchValue: string;
} => {
    const urlParams = new URLSearchParams(search);

    const page = Math.max(1, parseInt(urlParams.get("page") || "1", 10));
    const limit = Math.max(10, parseInt(urlParams.get("limit") || "10", 10));
    const sort = urlParams.get("sort") || "";
    const order = urlParams.get("order") || "ascending";
    const searchValue = urlParams.get("search") || "";

    const sortColumn = order === "descending" ? `-${sort || defaultSortColumn}` : (sort || defaultSortColumn);

    return {
        page,
        limit,
        sortColumn,
        searchValue,
    };
};

/**
 * Обновляет URL с текущими параметрами пагинации.
 * Формирует строку запроса на основе состояния и заменяет текущий URL без перезагрузки страницы.
 * @param history - Объект истории для навигации (из react-router)
 * @param location - Объект локации для получения текущего пути
 * @param state - Объект состояния с page, limit, sortColumn, searchValue
 */
export const updatePaginationUrl = (
    history: History,
    location: Location,
    state: { sortColumn: string; limit: number; page: number; searchValue: string | undefined },
    additionalFilters: Record<string, string | undefined> = {}
): void => {
    const { page, limit, sortColumn, searchValue } = state;

    const params = new URLSearchParams();

    if (page !== 1) params.set("page", page.toString());
    if (limit !== 10) params.set("limit", limit.toString());
    if (searchValue) params.set("search", searchValue);
    if (sortColumn) {
        const cleanSort = sortColumn.replace(/^-/, "");
        if (cleanSort) { // Не добавляем пустую сортировку
            params.set("sort", cleanSort);
            if (sortColumn.startsWith("-")) params.set("order", "descending");
        }
    }

    // Дополнительные фильтры
    Object.entries(additionalFilters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
            params.set(key, value);
        }
    });

    history.replace(`${location.pathname}?${params.toString()}`);
};

/**
 * Обрабатывает изменение сортировки при клике на заголовок таблицы.
 * Переключает направление сортировки (ascending/descending) для выбранной колонки.
 * @param currentSort - Текущее значение sortColumn (например, "name" или "-name")
 * @param column - Название колонки, по которой кликнули
 * @returns Новое значение sortColumn с учетом переключения направления сортировки
 */
export const handleSortPagination = (currentSort: string, column: string): string => {
    if (currentSort === column) {
        return currentSort.startsWith("-") ? column : `-${column}`;
    }
    return column;
};

/**
 * Преобразует sortColumn в формат, понятный API (например, '-surname' или 'name').
 * Унифицирует формат сортировки для передачи на сервер.
 * @param sortColumn - Значение сортировки (например, '-surname' или 'name')
 * @returns Строка для передачи в API (если начинается с '-', возвращает как есть, иначе возвращает колонку)
 */
export const getSortParamForApi = (sortColumn: string): string => {
    // Если уже начинается с '-', возвращаем как есть
    if (sortColumn.startsWith('-')) {
        return sortColumn;
    }
    // Иначе просто возвращаем колонку
    return sortColumn;
};